// src/prototype/components/sep/SEPSectionGovernance.tsx

import React from 'react';
import { ShieldCheck, CheckCircle2, Calendar } from 'lucide-react';

interface Finalist {
  id: string;
  title: string;
  implementationEffort: 'Low' | 'Medium' | 'High';
}

export interface SEPSectionGovernanceProps {
  finalists: Finalist[];
  clientName: string;
}

const APPROVAL_STEPS = [
  { step: 1, label: 'Strategic Review', owner: 'CDO / VP Analytics', description: 'Portfolio alignment and strategic fit confirmation with executive leadership.' },
  { step: 2, label: 'Financial Validation', owner: 'CFO / Finance', description: 'ROI model review, budget allocation approval, and payback period sign-off.' },
  { step: 3, label: 'Clinical Sign-Off', owner: 'CMIO / CMO', description: 'Clinical workflow impact assessment and operational readiness confirmation.' },
  { step: 4, label: 'IT Architecture Review', owner: 'CIO / IT Architecture', description: 'Infrastructure, data access, security, and integration pathway approval.' },
  { step: 5, label: 'Board / Exec Approval', owner: 'CEO / Board Committee', description: 'Final executive authorization and investment commitment for portfolio execution.' },
];

const CADENCE = [
  { freq: 'Weekly', type: 'Initiative Stand-up', participants: 'Project Leads, Data Team' },
  { freq: 'Bi-weekly', type: 'Steering Committee', participants: 'CDO, VP Analytics, PMO' },
  { freq: 'Monthly', type: 'Executive Briefing', participants: 'C-Suite, CFO, Board Liaison' },
  { freq: 'Quarterly', type: 'Portfolio Review', participants: 'Full Leadership Team' },
];

export const SEPSectionGovernance: React.FC<SEPSectionGovernanceProps> = ({ finalists, clientName }) => (
  <div className="bg-white border border-[#e5e7eb] rounded-xl p-6 mb-6">
    <div className="flex items-start gap-3 mb-6">
      <div className="w-[3px] self-stretch rounded-full bg-[#FF6E4C] shrink-0 min-h-[40px]" />
      <div>
        <p className="text-[10px] font-bold text-[#FF6E4C] uppercase tracking-widest mb-0.5">Section 8</p>
        <h2 className="text-lg font-bold text-[#18365E] leading-tight">Governance & Approval Framework</h2>
        <p className="text-[11px] text-[#717182] mt-0.5">Executive approval workflow and ongoing oversight cadence</p>
      </div>
    </div>

    <p className="text-sm text-[#18365E]/80 leading-relaxed mb-6">
      A structured governance model ensures that <strong>{clientName}</strong>'s initiative portfolio maintains executive visibility, financial accountability, and clinical alignment throughout execution. The framework below defines the approval workflow and ongoing oversight cadence.
    </p>

    {/* Approval workflow */}
    <div className="mb-6">
      <p className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide mb-3">Approval Workflow</p>
      <div className="relative">
        {/* Connector line */}
        <div className="absolute left-[18px] top-8 bottom-8 w-[2px] bg-[#e5e7eb] z-0" />
        <div className="space-y-3">
          {APPROVAL_STEPS.map((s, i) => {
            const isLast = i === APPROVAL_STEPS.length - 1;
            return (
              <div key={s.step} className="relative flex items-start gap-3 z-10">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 border-2 ${isLast ? 'bg-[#FF6E4C] border-[#FF6E4C]' : 'bg-white border-[#18365E]'}`}>
                  {isLast
                    ? <CheckCircle2 className="w-4 h-4 text-white" />
                    : <span className="text-xs font-bold text-[#18365E]">{s.step}</span>}
                </div>
                <div className="flex-1 bg-white border border-[#e5e7eb] rounded-xl p-3.5 hover:shadow-sm transition-shadow">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-semibold text-[#18365E]">{s.label}</p>
                    <span className="text-[10px] font-semibold text-[#717182] bg-[#F5F7FA] px-2 py-0.5 rounded-full border border-[#e5e7eb] shrink-0 whitespace-nowrap">{s.owner}</span>
                  </div>
                  <p className="text-xs text-[#717182] leading-relaxed mt-1">{s.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>

    {/* Cadence table */}
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Calendar className="w-3.5 h-3.5 text-[#717182]/60" />
        <p className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide">Oversight Cadence</p>
      </div>
      <div className="border border-[#e5e7eb] rounded-xl overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-[#F5F7FA] border-b border-[#e5e7eb]">
              <th className="text-left px-4 py-2.5 font-semibold text-[#717182] w-24">Frequency</th>
              <th className="text-left px-3 py-2.5 font-semibold text-[#717182]">Meeting Type</th>
              <th className="text-left px-3 py-2.5 font-semibold text-[#717182]">Participants</th>
            </tr>
          </thead>
          <tbody>
            {CADENCE.map((c, i) => (
              <tr key={i} className={i < CADENCE.length - 1 ? 'border-b border-[#e5e7eb]/60' : ''}>
                <td className="px-4 py-2.5">
                  <span className="text-[10px] font-bold px-2 py-0.5 bg-[#18365E]/[0.06] text-[#18365E] rounded-full border border-[#18365E]/10">{c.freq}</span>
                </td>
                <td className="px-3 py-2.5 font-medium text-[#18365E]">{c.type}</td>
                <td className="px-3 py-2.5 text-[#717182]">{c.participants}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

export default SEPSectionGovernance;